import type { ReactNode } from 'react';

import { BottomStickyPortal } from '@/components/app-layout/components/bottom-sticky-portal';
import { useFaultedDevices } from '@/features/equipment-browser/faulted-devices/hooks/use-faulted-devices';

import { EmptyResetFaultedTable } from './components/empty-reset-faulted-table';
import { ResetFaultedHeader } from './components/reset-faulted-header';
import { ResetFaultedSaveBar } from './components/reset-faulted-save-bar';
import { ResetFaultedSaveDialog } from './components/reset-faulted-save-dialog';
import { ResetFaultedTable } from './components/reset-faulted-table/reset-faulted-table';
import { useResetFaultedSave } from './hooks/use-reset-faulted-save';
import { useResetFaultedSelection } from './hooks/use-reset-faulted-selection';

interface ResetFaultedViewProps {
  onClose: () => void;
}

/**
 * Reset Faulted pane: every faulted device in All Equipment, with a save bar
 * that resets the selected ones. Devices leave the table on their own once their
 * live state is no longer faulted. Closing the pane never prompts to discard.
 * Its live values (fault conditions, fault reasons, modes) are streamed by the
 * equipment browser page, which stays subscribed while the drawer covers it.
 */
export const ResetFaultedView = ({ onClose }: ResetFaultedViewProps): ReactNode => {
  const devices = useFaultedDevices();
  const {
    deselectDevices,
    selectedDeviceIds,
    selectedDevices,
    setSelectedDeviceIds,
    setTargetMode,
    targetModes,
  } = useResetFaultedSelection(devices);

  const save = useResetFaultedSave({
    onDeselect: deselectDevices,
    selectedDevices,
    targetModes,
  });

  return (
    <div
      className="flex flex-col h-full overflow-hidden"
      data-component="ResetFaultedView"
      data-testid="reset-faulted-view"
    >
      <div className="shrink-0 bg-background border-b border-ckds-border-tier2 p-gutter-sm">
        <ResetFaultedHeader deviceCount={devices.length} onClose={onClose} />
      </div>

      <div className="flex-1 min-h-0 overflow-auto p-gutter-sm">
        {devices.length > 0 ? (
          <ResetFaultedTable
            devices={devices}
            onModeChange={setTargetMode}
            onSelectedDeviceIdsChange={setSelectedDeviceIds}
            selectedDeviceIds={selectedDeviceIds}
            targetModes={targetModes}
          />
        ) : (
          <EmptyResetFaultedTable />
        )}
      </div>

      <BottomStickyPortal>
        <ResetFaultedSaveBar
          onCancel={() => setSelectedDeviceIds([])}
          onReset={save.openDialog}
          selectedCount={selectedDevices.length}
        />
      </BottomStickyPortal>

      <ResetFaultedSaveDialog
        changes={save.changes}
        hasPendingSave={save.hasPendingSave}
        onCancelPending={save.cancelPending}
        onClose={save.closeDialog}
        onConfirm={save.confirmSave}
        onDiscard={save.discardChange}
        open={save.isDialogOpen}
        progressStats={save.progressStats}
        saveComplete={save.saveComplete}
        saveProgressByChangeId={save.saveProgressByChangeId}
      />
    </div>
  );
};
